import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, Empty, Row, Segmented, Select, Spin, Statistic, Table, Tag } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  CarOutlined,
  FieldTimeOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { Pie, Column } from '@ant-design/charts';
import { Link } from 'react-router-dom';
import * as partnerApi from '../modules/partners/api';
import './GlobalStats.scss';

// PERIOD_OPTIONS moved inside component to use translations

const STATUS_COLORS = {
  COMPLETED: '#2e7d32',
  DELIVERY_FAILED: '#c62828',
  SKIPPED: '#f57c00',
  ASSIGNED: '#1565c0',
  UNASSIGNED: '#9e9e9e',
  DISPATCHED: '#4527a0',
  COLLECTED: '#6a1b9a',
  PICKUP_ARRIVED: '#6a1b9a',
  DELIVERY_ARRIVED: '#6a1b9a',
  SERVICE_ARRIVED: '#6a1b9a',
  OPTIMIZED: '#00838f',
  ERROR: '#b71c1c',
  DRAFT: '#bdbdbd',
};

const CATEGORY_COLORS = {
  PRODUCT: '#1565c0',
  EMPTY: '#f57c00',
  UNKNOWN: '#9e9e9e',
};

const formatMins = (mins) => {
  if (!mins || mins <= 0) return '0m';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const GlobalStats = () => {
  const { t } = useTranslation('stats');

  const PERIOD_OPTIONS = [
    { label: t('periods.today'), value: 'today' },
    { label: t('periods.this_week'), value: 'this_week' },
    { label: t('periods.last_week'), value: 'last_week' },
    { label: t('periods.last_month'), value: 'last_month' },
    { label: t('periods.all'), value: 'all' },
  ];
  const [period, setPeriod] = useState('this_week');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);

  // Load partner options for the filter
  useEffect(() => {
    const loadPartners = async () => {
      const { response } = await partnerApi.getPartners({ take: 200 });
      if (response?.partners) {
        setPartnerOptions(response.partners.map((p) => ({ label: p.name, value: p.id })));
      }
    };
    loadPartners();
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    const { response } = await partnerApi.getGlobalStats(period, selectedPartnerIds);
    if (response) setStats(response);
    setLoading(false);
  }, [period, selectedPartnerIds]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Partner comparison table columns
  const partnerColumns = [
    {
      title: t('partnerName'),
      dataIndex: 'partnerName',
      key: 'partnerName',
      sorter: (a, b) => a.partnerName.localeCompare(b.partnerName),
      render: (name, record) => <Link to={`/admin/partners/${record.partnerId}/stats`}>{name}</Link>,
    },
    {
      title: t('ordersColumn'),
      dataIndex: 'orders',
      key: 'orders',
      sorter: (a, b) => a.orders - b.orders,
      defaultSortOrder: 'descend',
    },
    {
      title: t('completedColumn'),
      dataIndex: 'completed',
      key: 'completed',
      sorter: (a, b) => a.completed - b.completed,
    },
    {
      title: t('completionRateColumn'),
      dataIndex: 'completionRate',
      key: 'completionRate',
      sorter: (a, b) => a.completionRate - b.completionRate,
      render: (val) => {
        const color = val >= 80 ? '#2e7d32' : val >= 50 ? '#f57c00' : '#c62828';
        return <Tag color={color}>{val}%</Tag>;
      },
    },
    {
      title: t('routesColumn'),
      dataIndex: 'routes',
      key: 'routes',
      sorter: (a, b) => a.routes - b.routes,
    },
    {
      title: t('distanceKm'),
      dataIndex: 'totalDistanceKm',
      key: 'totalDistanceKm',
      sorter: (a, b) => a.totalDistanceKm - b.totalDistanceKm,
      render: (val) => val.toLocaleString(),
    },
    {
      title: t('itemsColumn'),
      dataIndex: 'items',
      key: 'items',
      sorter: (a, b) => a.items - b.items,
    },
  ];

  if (loading && !stats) {
    return (
      <div className="GlobalStatsPage Content">
        <Spin style={{ display: 'block', paddingTop: 80 }} />
      </div>
    );
  }

  // Chart data
  const statusData = stats
    ? Object.entries(stats.time.ordersByStatus).map(([status, value]) => ({
        type: status.replace(/_/g, ' '),
        value,
        color: STATUS_COLORS[status] || '#9e9e9e',
      }))
    : [];

  const onTimeData = stats
    ? [
        { type: 'On time', value: stats.time.onTimeCount, color: '#2e7d32' },
        { type: 'Late', value: stats.time.lateCount, color: '#c62828' },
      ].filter((d) => d.value > 0)
    : [];

  const categoryData = stats
    ? Object.entries(stats.collection.byCategory).map(([cat, value]) => ({
        type: cat,
        value,
        color: CATEGORY_COLORS[cat] || '#9e9e9e',
      }))
    : [];

  const dowOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dowData = stats ? dowOrder.map((day) => ({ day, orders: stats.time.ordersByDayOfWeek[day] || 0 })) : [];

  const pieConfig = {
    height: 240,
    innerRadius: 0.6,
    radius: 0.85,
    label: { text: 'value', style: { fontWeight: 600 } },
    legend: { color: { position: 'bottom', layout: { justifyContent: 'center' } } },
    tooltip: { title: 'type' },
    style: { lineWidth: 2, stroke: '#fff' },
  };

  return (
    <div className="GlobalStatsPage Content">
      <div className="ContentHeader">
        <div className="HeaderTitle">{t('title')}</div>
      </div>

      <div className="partner-stats global-stats">
        <div className="global-stats-controls">
          <Segmented options={PERIOD_OPTIONS} value={period} onChange={setPeriod} className="period-selector" />
          <Select
            mode="multiple"
            placeholder={t('allPartners')}
            value={selectedPartnerIds}
            onChange={setSelectedPartnerIds}
            options={partnerOptions}
            style={{ minWidth: 280 }}
            maxTagCount={3}
            allowClear
            showSearch
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </div>

        {!stats || stats.delivery.total === 0 ? (
          loading ? (
            <Spin style={{ display: 'block', paddingTop: 80 }} />
          ) : (
            <Empty description="No data for this period" style={{ paddingTop: 60 }} />
          )
        ) : (
          <Spin spinning={loading}>
            {/* Delivery Performance */}
            <div className="stats-section">
              <div className="section-title">Delivery Performance</div>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={8} md={4}>
                  <Card className="stat-card">
                    <Statistic title="Total" value={stats.delivery.total} valueStyle={{ color: '#212121' }} />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card className="stat-card success">
                    <Statistic
                      title="Completed"
                      value={stats.delivery.completed}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: '#2e7d32' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card className="stat-card danger">
                    <Statistic
                      title="Failed"
                      value={stats.delivery.failed}
                      prefix={<CloseCircleOutlined />}
                      valueStyle={{ color: '#c62828' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card className="stat-card warning">
                    <Statistic
                      title="Skipped"
                      value={stats.delivery.skipped}
                      prefix={<MinusCircleOutlined />}
                      valueStyle={{ color: '#f57c00' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card className="stat-card">
                    <Statistic title="Completion Rate" value={stats.delivery.completionRate} suffix="%" precision={1} />
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Charts Row 1 */}
            <Row gutter={[16, 16]} className="stats-section">
              <Col xs={24} md={12}>
                <Card className="chart-card" title="Orders by Status">
                  {statusData.length > 0 ? (
                    <Pie
                      data={statusData}
                      angleField="value"
                      colorField="type"
                      color={statusData.map((d) => d.color)}
                      {...pieConfig}
                    />
                  ) : (
                    <Empty description="No data" />
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card className="chart-card" title="On-Time Performance">
                  {onTimeData.length > 0 ? (
                    <Pie
                      data={onTimeData}
                      angleField="value"
                      colorField="type"
                      color={['#2e7d32', '#c62828']}
                      {...pieConfig}
                    />
                  ) : (
                    <Empty description="No delivery time data" />
                  )}
                </Card>
              </Col>
            </Row>

            {/* Route Operations */}
            <div className="stats-section">
              <div className="section-title">Route Operations</div>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic title="Routes" value={stats.routes.total} prefix={<CarOutlined />} />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic title="Avg Stops / Route" value={stats.routes.avgStopsPerRoute} precision={1} />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic title="Total Distance" value={stats.routes.totalDistanceKm} suffix="km" precision={1} />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic
                      title="Avg Duration"
                      value={formatMins(stats.routes.avgDurationMins)}
                      prefix={<FieldTimeOutlined />}
                      formatter={(val) => val}
                    />
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Charts Row 2 */}
            <Row gutter={[16, 16]} className="stats-section">
              <Col xs={24} md={12}>
                <Card className="chart-card" title="Items by Category">
                  {categoryData.length > 0 ? (
                    <Pie
                      data={categoryData}
                      angleField="value"
                      colorField="type"
                      color={categoryData.map((d) => d.color)}
                      {...pieConfig}
                    />
                  ) : (
                    <Empty description="No items collected" />
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card className="chart-card" title="Orders by Day of Week">
                  <Column
                    data={dowData}
                    xField="day"
                    yField="orders"
                    height={240}
                    color="#212121"
                    label={{ position: 'top', style: { fontWeight: 600 } }}
                    xAxis={{ label: { style: { fontSize: 12 } } }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Collection & Time */}
            <div className="stats-section">
              <div className="section-title">Collection & Time</div>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic title="Items Collected" value={stats.collection.totalItems} prefix={<InboxOutlined />} />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic title="Items / Route" value={stats.collection.avgItemsPerRoute} precision={1} />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic
                      title="Avg Time / Stop"
                      value={formatMins(stats.time.avgTimePerStopMins)}
                      formatter={(val) => val}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic
                      title="Return Drop-off"
                      value={stats.collection.returnDropoffRate}
                      suffix="%"
                      precision={1}
                    />
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Partner Comparison Table */}
            {stats.byPartner && stats.byPartner.length > 0 && (
              <div className="stats-section">
                <div className="section-title">Partner Comparison</div>
                <Card className="partner-table-card">
                  <Table
                    dataSource={stats.byPartner}
                    columns={partnerColumns}
                    rowKey="partnerId"
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content' }}
                  />
                </Card>
              </div>
            )}
          </Spin>
        )}
      </div>
    </div>
  );
};

export default GlobalStats;
